import { ref, onUnmounted, nextTick } from 'vue';
import type { GreCaptchaV2, UseRecaptchaV2Return } from '../types/recaptcha';
import { logger } from '@/shared/common/utils/logger.util';

export function useRecaptchaV2(siteKey: string): UseRecaptchaV2Return {
  const widgetId = ref<number | null>(null);
  let resolver: ((value: string) => void) | null = null;
  let recaptchaScriptPromise: Promise<boolean> | null = null;
  let lastContainerId: string | null = null;
  let domCleanupGeneration = 0;
  let executionGeneration = 0;

  function scheduleRecaptchaDomCleanup(containerId: string): void {
    const generation = domCleanupGeneration;

    const clearDom = (): void => {
      if (generation !== domCleanupGeneration) return;
      clearRecaptchaContainer(containerId);
    };

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        requestAnimationFrame(clearDom);
      });
      return;
    }

    clearDom();
  }

  function clearRecaptchaContainer(elementId: string): void {
    const container = document.getElementById(elementId);
    container?.replaceChildren();
  }

  function createRecaptchaMountNode(container: HTMLElement): HTMLElement {
    const mountNode = document.createElement('div');
    container.replaceChildren(mountNode);
    return mountNode;
  }

  function isWidgetMountedInDom(element: HTMLElement): boolean {
    return element.querySelector('iframe') !== null;
  }

  const getGrecaptcha = (): GreCaptchaV2 | null => {
    const recaptcha = (window as any).grecaptcha as GreCaptchaV2 | undefined;
    return recaptcha && typeof recaptcha.render === 'function'
      ? recaptcha
      : null;
  };

  const loadScript = (): Promise<boolean> => {
    if (getGrecaptcha()) return Promise.resolve(true);
    if (recaptchaScriptPromise) return recaptchaScriptPromise;
    recaptchaScriptPromise = new Promise((resolve) => {
      if (document.querySelector('script[data-recaptcha="v2"]')) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.recaptcha = 'v2';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.head.appendChild(script);
    });
    return recaptchaScriptPromise;
  };

  function syncInvalidateWidget(containerId: string): void {
    const grecaptcha = getGrecaptcha();
    const widgetIdToReset = widgetId.value;
    const pendingResolver = resolver;

    executionGeneration += 1;
    widgetId.value = null;
    resolver = null;
    clearRecaptchaContainer(containerId);

    if (pendingResolver) {
      pendingResolver('');
    }

    if (grecaptcha && widgetIdToReset !== null) {
      try {
        grecaptcha.reset(widgetIdToReset);
      } catch (e) {
        logger.warn('[recaptcha] V2 reset failed during invalidate', {
          error: (e as Error).message,
        });
      }
    }
  }

  const renderRecaptcha = async (
    elementId: string,
    generation: number
  ): Promise<boolean> => {
    const loaded = await loadScript();
    if (!loaded) return false;

    await nextTick();
    const grecaptcha = getGrecaptcha();
    const el = document.getElementById(elementId);
    if (!grecaptcha || !el) return false;

    const getRecaptchaSize = (element: HTMLElement): 'normal' | 'compact' => {
      const width = element.clientWidth || window.innerWidth;
      return width < 360 ? 'compact' : 'normal';
    };

    if (isWidgetMountedInDom(el)) {
      clearRecaptchaContainer(elementId);
    }

    if (widgetId.value !== null) {
      widgetId.value = null;
    }

    const handleResult = (value: string) => {
      if (generation !== executionGeneration) return;

      if (resolver) {
        const r = resolver;
        resolver = null;
        r(value);
      }
    };

    try {
      const mountNode = createRecaptchaMountNode(el);
      widgetId.value = grecaptcha.render(mountNode, {
        sitekey: siteKey,
        size: getRecaptchaSize(el),
        callback: (response: string) => handleResult(response),
        'error-callback': () => handleResult(''),
        'expired-callback': () => handleResult(''),
      });
    } catch (e) {
      logger.warn('[recaptcha] V2 render failed', {
        error: (e as Error).message,
      });
      clearRecaptchaContainer(elementId);
      widgetId.value = null;
      return false;
    }

    return true;
  };

  const execute = async (containerId?: string): Promise<string> => {
    if (!containerId) {
      return '';
    }

    domCleanupGeneration += 1;
    lastContainerId = containerId;
    syncInvalidateWidget(containerId);
    executionGeneration += 1;
    const generation = executionGeneration;

    return new Promise<string>(async (resolve) => {
      resolver = (value: string) => resolve(value);

      const rendered = await renderRecaptcha(containerId, generation);
      if (!rendered && generation === executionGeneration) {
        resolver = null;
        resolve('');
      }
    });
  };

  const cleanup = (): void => {
    const grecaptcha = getGrecaptcha();
    const widgetIdToReset = widgetId.value;
    const containerId = lastContainerId;
    const pendingResolver = resolver;

    executionGeneration += 1;
    domCleanupGeneration += 1;
    widgetId.value = null;
    resolver = null;
    lastContainerId = null;

    if (pendingResolver) {
      pendingResolver('');
    }

    if (grecaptcha && widgetIdToReset !== null) {
      try {
        grecaptcha.reset(widgetIdToReset);
      } catch (e) {
        logger.warn('[recaptcha] V2 cleanup reset failed', {
          error: (e as Error).message,
        });
      }
    }

    if (containerId) {
      scheduleRecaptchaDomCleanup(containerId);
    }
  };

  onUnmounted(cleanup);

  return { execute, cleanup };
}
